import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cva } from 'class-variance-authority';
import { cn } from '../lib/cn.js';
const badgeVariants = cva('inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium', {
    variants: {
        tone: {
            neutral: 'border-transparent bg-surface-muted text-text-muted',
            success: 'border-transparent bg-success/15 text-success',
            warning: 'border-transparent bg-warning/15 text-warning',
            danger: 'border-transparent bg-destructive/15 text-destructive',
            info: 'border-transparent bg-info/15 text-info',
        },
    },
    defaultVariants: { tone: 'neutral' },
});
/**
 * A status indicator.
 *
 * The dot is decorative and hidden from assistive technology; colour alone
 * never carries the meaning. WCAG 1.4.1 is explicit about this, and it is also
 * the difference between a colour-blind user reading "cancelled" and reading a
 * grey pill that looks much like the green one next to it.
 */
export function StatusBadge({ children, tone = 'neutral', className, }) {
    return (_jsxs("span", { className: cn(badgeVariants({ tone }), className), children: [_jsx("span", { "aria-hidden": "true", className: cn('h-1.5 w-1.5 rounded-full', {
                    'bg-muted-foreground': tone === 'neutral',
                    'bg-success': tone === 'success',
                    'bg-warning': tone === 'warning',
                    'bg-destructive': tone === 'danger',
                    'bg-info': tone === 'info',
                }) }), children] }));
}
/**
 * The tone a platform status is shown in.
 *
 * Kept here rather than at each call site so that a cancelled reservation and
 * a cancelled payment look the same everywhere. Statuses come from
 * `@ibe/constants`, but this package deliberately does not import them: a
 * component library that knows the reservation lifecycle is no longer a
 * component library. The mapping is by convention on the status string, and
 * anything unrecognised falls back to neutral rather than throwing — a new
 * status added by a backend release must not blank an admin screen.
 */
const TONE_BY_STATUS = {
    ACTIVE: 'success',
    CONFIRMED: 'success',
    CAPTURED: 'success',
    COMPLETED: 'success',
    DELIVERED: 'success',
    SETTLED: 'success',
    SENT: 'success',
    PENDING: 'warning',
    PENDING_PAYMENT: 'warning',
    AUTHORIZED: 'warning',
    PROCESSING: 'warning',
    QUEUED: 'warning',
    ON_HOLD: 'warning',
    IN_PROGRESS: 'warning',
    CANCELLED: 'danger',
    FAILED: 'danger',
    DECLINED: 'danger',
    EXPIRED: 'danger',
    BOUNCED: 'danger',
    ERROR: 'danger',
    DRAFT: 'neutral',
    INACTIVE: 'neutral',
    ARCHIVED: 'neutral',
    SUPPRESSED: 'neutral',
    REFUNDED: 'info',
    PARTIALLY_REFUNDED: 'info',
    MODIFIED: 'info',
};
export function toneForStatus(status) {
    return TONE_BY_STATUS[status.toUpperCase()] ?? 'neutral';
}
//# sourceMappingURL=status-badge.js.map